import { events } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useEffect, useState } from 'react';
import { initializeAmplify } from 'src/utils/auth';
import { useAppSelector } from 'src/utils/Hooks';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';

export type NotificationEvent = {
  eventType: string;
  [x: string]: any;
};

export type DataFormatters = {
  presentations: (event: NotificationEvent) => void;
  export: (event: NotificationEvent) => void;
  adjustments: (event: NotificationEvent) => void;
};

export const useNotificationSubscriber = () => {
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const session = useAppSelector((state) => state.user.session);
  const { setPresentationBadge } = useNotificationsBadge();
  const [dataFormatters, setDataFormatters] = useState<DataFormatters>({
    presentations: (event: NotificationEvent) => {},
    export: (event: NotificationEvent) => {},
    adjustments: (event: NotificationEvent) => {}
  });

  const handlePresentationsEvent = useCallback((event: NotificationEvent) => {
    if (event.status === 'COMPLETE') {
      setPresentationBadge(true);
    }
    dataFormatters.presentations(event);
  }, [dataFormatters, setPresentationBadge]);

  useEffect(() => {
    if (!session?.userId) return;

    if (currentSubscription) {
      currentSubscription.close();
    }

    initializeAmplify();

    const subscribeToEvents = async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const token = tokens?.idToken?.toString();

        const pr = await events.connect(`/default/userId/${session?.userId}`, {
          authMode: 'lambda',
          authToken: JSON.stringify({
            token,
            clientEmail: session?.email
          })
        });

        pr.subscribe({
          next: (data) => {
            const event = data.event;
            switch (event.eventType) {
              case 'presentations_status':
                handlePresentationsEvent(event);
                break;
              default:
                break;
            }
          },
          error: (err) => console.error('Notifications Socket Error:', err),
        });

        return pr;
      } catch (err) {
        console.error('Notifications Socket Error:', err);
      }
    }

    subscribeToEvents().then(subscription => {
      if (subscription) {
        setCurrentSubscription(subscription);
      }
    });

    return () => {
      if (currentSubscription) {
        currentSubscription.close();
      }
    }
  }, [session, handlePresentationsEvent]);

  return { currentSubscription, setDataFormatters };
}
